import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, VStack, HStack, Button, Spinner, Alert, AlertIcon,
  RadioGroup, Radio, FormControl, FormLabel, FormHelperText, Input, Badge,
  Divider, useToast,
} from '@chakra-ui/react';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('assistant_token')}`,
});

function LLMProvider() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [provider, setProvider] = useState('ollama');
  const [baseUrl, setBaseUrl] = useState('');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [isManagedConfigured, setIsManagedConfigured] = useState(false);

  const fetchSettings = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/llm-provider/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setProvider(data.provider);
        setBaseUrl(data.managed_base_url || '');
        setModel(data.managed_model || '');
        setApiKeyMasked(data.managed_api_key_masked || '');
        setIsManagedConfigured(data.is_managed_configured);
        setApiKey('');
        setError(null);
      })
      .catch(() => setError('Could not load AI provider settings.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { provider, managed_base_url: baseUrl, managed_model: model };
      if (apiKey) body.managed_api_key = apiKey;

      const res = await fetch(`${API_BASE}/llm-provider/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (!res.ok || data.success === false) {
        toast({ title: data.message || 'Failed to save', status: 'error', duration: 5000 });
        return;
      }

      setApiKeyMasked(data.managed_api_key_masked || '');
      setIsManagedConfigured(data.is_managed_configured);
      setApiKey('');
      toast({
        title: 'Saved',
        description: `Every active chat session now uses ${provider === 'managed' ? 'the managed provider' : 'the self-hosted backend'}.`,
        status: 'success',
        duration: 4000,
      });
    } catch (e) {
      toast({ title: 'Error saving settings', description: e.message, status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="purple.500" /></Box>;

  return (
    <Box maxW="640px">
      <Heading size="lg" mb={2}>AI Provider</Heading>
      <Text color="gray.500" mb={6}>
        Choose which backend answers chat messages. This is a single global
        setting — switching it changes the backend for every user of the
        assistant immediately.
      </Text>

      {error && <Alert status="error" mb={4} borderRadius="md"><AlertIcon />{error}</Alert>}

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <FormControl mb={6}>
          <FormLabel fontWeight="semibold">Backend</FormLabel>
          <RadioGroup value={provider} onChange={setProvider}>
            <VStack align="start" spacing={3}>
              <Radio value="ollama">
                Self-hosted (Ollama) — runs on our own infrastructure
              </Radio>
              <Radio value="managed">
                Managed (OpenAI-compatible, e.g. DigitalOcean Gradient AI)
              </Radio>
            </VStack>
          </RadioGroup>
        </FormControl>

        {provider === 'managed' && (
          <>
            <Divider mb={6} />
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Text fontWeight="semibold">Managed provider configuration</Text>
                <Badge colorScheme={isManagedConfigured ? 'green' : 'orange'}>
                  {isManagedConfigured ? 'Configured' : 'Incomplete'}
                </Badge>
              </HStack>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Base URL</FormLabel>
                <Input
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://inference.do-ai.run/v1"
                  fontFamily="mono"
                  fontSize="sm"
                />
                <FormHelperText>OpenAI-compatible API base — from your DigitalOcean Gradient AI dashboard.</FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Model</FormLabel>
                <Input
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="e.g. kimi-k2-instruct"
                  fontFamily="mono"
                  fontSize="sm"
                />
                <FormHelperText>Exact model name/slug as listed in the provider's model catalog.</FormHelperText>
              </FormControl>

              <FormControl isRequired={!apiKeyMasked}>
                <FormLabel fontSize="sm">
                  API Key {apiKeyMasked && <Text as="span" color="gray.500" fontWeight="normal">(leave blank to keep current: {apiKeyMasked})</Text>}
                </FormLabel>
                <Input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={apiKeyMasked ? '••••••••' : 'Paste your API key'}
                  fontFamily="mono"
                  fontSize="sm"
                />
              </FormControl>
            </VStack>
          </>
        )}

        <Button
          mt={6}
          colorScheme="purple"
          onClick={handleSave}
          isLoading={saving}
          loadingText="Saving..."
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}

export default LLMProvider;
